/**
 * Page-level pieces of the School Journey brand (Brand Guidelines v1.0): the
 * eyebrow, the swoop under a headline's key phrase, section headings, pill
 * buttons and the navy "Book a demo" surface. Server-safe: no hooks.
 */

import type { ReactNode } from 'react'
import { ArrowRight } from 'lucide-react'

import { cn } from '@/lib/utils'

/** The signature gradient: two layers, always together (page 10). */
export const BRAND_GRADIENT =
  'radial-gradient(ellipse at bottom left, #0F3D5F 0%, transparent 55%), linear-gradient(100deg, #0F4A72 0%, #0F517B 45%, #1D6693 100%)'

/**
 * The card hover (Sep 2026): white at rest, the brand navy fades in on hover, and
 * the accents (icon circle, checks, arrows) turn marker yellow to echo the gold
 * button hover. Put `hoverCard` on the card, `<HoverCardGlow />` as its first
 * child (it sits behind the content, no wrapper needed), then tag parts with the hc* classes.
 */
export const hoverCard =
  'group/card relative isolate overflow-hidden transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#0F3D5F]/20 motion-reduce:hover:translate-y-0'
export const hcIcon = 'transition-colors duration-300 group-hover/card:bg-[#FFC933] group-hover/card:text-[#0F3D5F]'
export const hcTitle = 'transition-colors duration-300 group-hover/card:text-white'
export const hcText = 'transition-colors duration-300 group-hover/card:text-[#D6E6F2]'
export const hcAccent = 'transition-colors duration-300 group-hover/card:text-[#FFC933]'
export const hcDot = 'transition-colors duration-300 group-hover/card:bg-[#FFC933]'

export function HoverCardGlow() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10 opacity-0 transition-opacity duration-300 ease-out group-hover/card:opacity-100"
      style={{ background: BRAND_GRADIENT }}
    />
  )
}

/** The arrow on a primary pill: glides right on hover (the parent carries `group/pill`). */
export function PillArrow({ className }: { className?: string }) {
  return (
    <ArrowRight
      aria-hidden="true"
      strokeWidth={2.5}
      className={cn(
        'size-4 shrink-0 transition-transform duration-300 ease-out group-hover/pill:translate-x-1.5 motion-reduce:transition-none motion-reduce:group-hover/pill:translate-x-0',
        className
      )}
    />
  )
}

/** Buttons are full pills (page 12). One primary per view. */
export const pillPrimary =
  'inline-flex items-center justify-center gap-2 rounded-full bg-[#0F517B] px-6 py-3 text-sm font-semibold text-white shadow-sm group/pill transition-[background-color,color,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#FFC933] hover:text-[#0F3D5F] hover:shadow-lg hover:shadow-[#0F3D5F]/25 motion-reduce:transition-colors motion-reduce:hover:translate-y-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F517B] dark:bg-[#F8FAFC] dark:text-[#0F517B] dark:hover:bg-[#FFC933] dark:focus-visible:outline-[#BAD7EC]'
export const pillSecondary =
  'inline-flex items-center justify-center gap-2 rounded-full border border-[#0F517B]/25 bg-white px-6 py-3 text-sm font-semibold text-[#0F517B] transition-colors hover:border-[#0F517B]/50 hover:bg-[#F8FAFC] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F517B] dark:border-white/25 dark:bg-transparent dark:text-white dark:hover:bg-white/10 dark:focus-visible:outline-[#BAD7EC]'
export const pillOnNavy =
  'inline-flex items-center justify-center gap-2 rounded-full bg-[#F8FAFC] px-6 py-3 text-sm font-semibold text-[#0F517B] shadow-sm group/pill transition-[background-color,color,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#FFC933] hover:text-[#0F3D5F] hover:shadow-lg hover:shadow-[#0F3D5F]/25 motion-reduce:transition-colors motion-reduce:hover:translate-y-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'

/** Inter Bold capitals, 0.2em tracking: light navy on white, sky on navy. */
export function Eyebrow({
  children,
  onNavy,
  className
}: {
  children: ReactNode
  onNavy?: boolean
  className?: string
}) {
  return (
    <p
      className={cn(
        'text-xs font-bold tracking-[0.2em] uppercase',
        onNavy ? 'text-[#BAD7EC]' : 'text-[#1D6693] dark:text-[#BAD7EC]',
        className
      )}
    >
      {children}
    </p>
  )
}

/**
 * The swoop from the logo, under a headline's key phrase. One per page, never
 * under body text (page 7).
 */
export function Swooped({ children, onNavy }: { children: ReactNode; onNavy?: boolean }) {
  return (
    <span className="relative inline-block whitespace-nowrap">
      {children}
      <svg
        aria-hidden="true"
        viewBox="126 482 576 106"
        preserveAspectRatio="none"
        className={cn(
          'absolute -bottom-[0.2em] left-[-2%] h-[0.26em] w-[104%]',
          onNavy ? 'text-[#BAD7EC]' : 'text-[#0F517B] dark:text-[#BAD7EC]'
        )}
      >
        <path
          fill="currentColor"
          d="M507.28,492.43c46.7-1.25,98.17.36,144.56,5.7,7.58.87,29.15,3.01,34.84,6.12,8.39,4.6,8.06,17.02.56,22.26-4.6,3.22-8.97,2.84-14.41,2.56-14.45-.76-30.24-3.88-44.97-4.97-161.09-11.94-320.17,14.65-475.65,53.84-15.75,1.44-23.31-21.18-5.35-28.35,18.29-7.31,49.94-13.17,70.09-17.82,94.98-21.9,192.59-36.73,290.33-39.34Z"
        />
      </svg>
    </span>
  )
}

/** Cal Sans headings in navy: display 48px / 1.1, heading 30px / 1.15 (page 11). */
export const headingDisplay =
  'font-heading text-4xl leading-[1.1] tracking-tight text-[#0F517B] sm:text-5xl lg:text-[3.5rem] dark:text-white'
export const headingSection =
  'font-heading text-3xl leading-[1.15] tracking-tight text-[#0F517B] sm:text-4xl dark:text-white'
export const bodyLead = 'text-lg leading-relaxed text-[#0F1B2D]/80 dark:text-slate-300'
export const bodyText = 'text-base leading-relaxed text-[#0F1B2D]/75 dark:text-slate-300'

export function SectionHeader({
  eyebrow,
  title,
  lead,
  align = 'center',
  className
}: {
  eyebrow?: ReactNode
  title: ReactNode
  lead?: ReactNode
  align?: 'center' | 'left'
  className?: string
}) {
  return (
    <div className={cn('mb-12 max-w-3xl', align === 'center' && 'mx-auto text-center', className)}>
      {eyebrow && <Eyebrow className="mb-3">{eyebrow}</Eyebrow>}
      <h2 className={headingSection}>{title}</h2>
      {lead && <p className={cn(bodyLead, 'mt-4')}>{lead}</p>}
    </div>
  )
}

/** A marketing page's opening: eyebrow, Cal Sans headline (with its one swoop) and lead. */
export function PageHero({
  eyebrow,
  title,
  lead,
  children,
  className
}: {
  eyebrow?: ReactNode
  title: ReactNode
  lead?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <section className={cn('container max-w-5xl pt-10 pb-12 text-center md:pt-16 md:pb-16', className)}>
      {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
      <h1 className={headingDisplay}>{title}</h1>
      {lead && <p className={cn(bodyLead, 'mx-auto mt-6 max-w-2xl')}>{lead}</p>}
      {children}
    </section>
  )
}

/** The navy surface from the Book a demo block: gradient, 12px corners. */
export function NavyPanel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn('relative overflow-hidden rounded-xl text-[#F8FAFC]', className)}
      style={{ background: BRAND_GRADIENT }}
    >
      {children}
    </div>
  )
}
